import { useRouter } from 'next/router'
import { useMutation } from '@tanstack/react-query'
import {
  CertificateProgramEngagementCta,
  CertificateProgramEngagementActionType,
  CertificateProgramEnrollmentService,
} from '@mc/api'
import { ButtonKindValue } from '@mc/design-system'
import Experiment from '@mc/experiments-react'
import { paths } from '@mc/paths'
import {
  useCertificateSlug,
  useIsEnterpriseMember,
} from 'pages/certificates/common'
import { MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG } from 'pages/certificates/experiments/mccerts-1928-secondary-cta-removal'
import { useSignup } from '../SignUpButton'
import {
  PurchaseCertificateCTA,
  DownloadSyllabusCTA,
  StartTrialCTA,
} from './BaseCTAs'

interface EngagementCTAProps {
  action: CertificateProgramEngagementCta
  setShowSyllabusModal: (show: boolean) => void
  certUuid: string
}

export const EngagementCTA = ({
  action,
  setShowSyllabusModal,
  certUuid,
}: EngagementCTAProps) => {
  const router = useRouter()
  const { certSlug } = useCertificateSlug()
  const { fullHref, trackQuizExposure } = useSignup({})
  const isEnterprise = useIsEnterpriseMember()

  const { mutate: partialEnrollment } = useMutation(
    () =>
      CertificateProgramEnrollmentService.enrollInCertificateProgram({
        certificateProgramUuid: certUuid,
        requestBody: {
          autoCheckout: false,
        },
      }),
    {
      onSuccess: () => router.push(paths.certificateOverview(certSlug)),
    },
  )

  const { text, actionType } = action

  if (
    actionType === CertificateProgramEngagementActionType.PURCHASE_CERTIFICATE
  ) {
    if (isEnterprise) return null

    return (
      <PurchaseCertificateCTA
        text={text}
        href={fullHref}
        onClick={trackQuizExposure}
        state={action.state}
        style={action.style as ButtonKindValue}
      />
    )
  }

  if (actionType === CertificateProgramEngagementActionType.DOWNLOAD_SYLLABUS) {
    return (
      <Experiment
        name={MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG}
        track={false}
        control={() => (
          <DownloadSyllabusCTA
            text={text}
            onClick={() => {
              setShowSyllabusModal(true)
            }}
            state={action.state}
            style={action.style as ButtonKindValue}
          />
        )}
        variant_1={() => null}
        variant_2={() => null}
      />
    )
  }

  if (actionType === CertificateProgramEngagementActionType.START_TRIAL) {
    return (
      <StartTrialCTA
        text={text}
        onClick={() => {
          partialEnrollment()
        }}
      />
    )
  }

  return null
}
